import { useEffect, useMemo } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { CircleCheckIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import Spinner from "components/Spinner";

import { ReactComponent as PriorityHigh } from "assets/priority_high.svg";

export default function AuthCallback() {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const urlCode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const errorDescription = params.get("error_description");
    return errorDescription?.replace(/^\[|\]$/g, "");
  }, [location.search]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  const renderStatusCard = (
    isSuccess: boolean,
    titleKey: string,
    messageKey: string,
  ) => (
    <div className="flex items-center justify-center min-h-screen bg-general-mint-700 p-4">
      <div className="flex flex-col items-center gap-4 w-full max-w-[400px] p-10 bg-white rounded-md">
        <div
          className={`w-20 h-20 m-auto mb-5 rounded-full flex items-center justify-center ${
            isSuccess
              ? "bg-general-gray-200 border border-general-gray-400"
              : "bg-general-orange-700 border border-general-orange-800"
          }`}
        >
          {isSuccess ? (
            <CircleCheckIcon className="w-12 h-12 text-general-green-800" />
          ) : (
            <PriorityHigh className="w-16 h-16 fill-general-orange-900" />
          )}
        </div>
        <h1 className="text-center text-3xl font-bold">{t(titleKey)}</h1>
        <div className="text-center text-general-gray-700">{t(messageKey)}</div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (urlCode === "100") {
    return renderStatusCard(
      true,
      "auth.registrationSuccess",
      "auth.100",
    );
  }

  if (urlCode === "101") {
    return renderStatusCard(
      false,
      "auth.registrationUnsuccessful",
      "auth.101",
    );
  }

  if (error) {
    return renderStatusCard(
      false,
      "auth.error",
      "auth.generalError",
    );
  }
}
